import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { Building2, ArrowRight } from "lucide-react";

interface CaseStudiesSectionProps {
  items: any[];
  isLoading: boolean;
}

export function CaseStudiesSection({ items, isLoading }: CaseStudiesSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 3) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border bg-neutral-50/50" aria-labelledby="case-studies-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Case Studies"
          icon={Building2}
          title="Case Studies"
          href="/case-studies"
        />
        {isLoading ? (
          <div className="grid gap-6 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 lg:grid-cols-3">
            {displayItems.map((item: any) => (
              <Card key={item.id} className="group hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-3">
                    {typeof item.description === "string" ? item.description.slice(0, 120) : ""}
                  </p>
                  <div className="mt-4">
                    <Button variant="ghost" size="sm" asChild className="p-0 h-auto text-xs gap-1">
                      <Link href={`/case-studies`}>
                        Read case study <ArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={Building2} description="No case studies yet." />
        )}
      </div>
    </section>
  );
}